/**
 * BookFormatterApplication - Book Formatting Application
 * 
 * Extends BaseApplication to provide book formatting functionality
 * for KDP-ready manuscript preparation
 */

import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig } from '../../../services/baseApplication/types';
import { BOOK_FORMATTER_APP_CONFIG, DEFAULT_FORMAT_SETTINGS } from './BookFormatterConfig';
import { entityService } from '../../../services/entityService';
import { createCollaborationProvider } from '../../../services/collaboration';
import { authService } from '../../../services/auth/authService';
import { generateUserColor } from '../../shared/appHelpers';
import { BookProjectSkeleton } from '../../../types';
import { WEBSOCKET_URL } from '../../../api/config';
import { BookCover, CoverLayer, BackgroundLayer, TextLayer } from '../types/cover';
import { API_ORIGIN_URL } from '../../../api/config';

/**
 * Recursively extract plain text from a Y.js XmlFragment or XmlElement.
 * Uses toDelta() on XmlText nodes (same approach as the backend yjs_to_markdown.js)
 * so non-text objects like images are silently skipped.
 */
function extractYjsText(node: Y.XmlFragment | Y.XmlElement): string {
  const children = (node as any).toArray() as Array<Y.XmlText | Y.XmlElement>;
  let text = '';
  for (const child of children) {
    if (child instanceof Y.XmlText) {
      for (const op of (child as Y.XmlText).toDelta()) {
        if (typeof op.insert === 'string') text += op.insert;
      }
    } else if (child instanceof Y.XmlElement) {
      const inner = extractYjsText(child as Y.XmlElement);
      const name = (child as Y.XmlElement).nodeName;
      if (name === 'paragraph' || name === 'heading') {
        text += inner + '\n\n';
      } else if (name === 'listItem' || name === 'list_item') {
        text += inner.trim() + '\n';
      } else {
        text += inner;
      }
    }
  }
  return text;
}

/**
 * Create a default cover using the bundled cover photo.
 * Darkening is applied via imageFilters.brightness (no overlay shapes).
 * Typography uses Inter (already loaded) — large, bold, white.
 */
function createDefaultCover(bookProjectId: string, title: string, author: string): BookCover {
  const t = Date.now();
  return {
    id: `cover-${t}`,
    bookProjectId,
    source: 'custom',
    dimensions: { width: 1600, height: 2400, dpi: 300 },
    layers: [
      // Background photo — natural, no filters
      {
        id: `layer-bg-${t}`,
        type: 'background',
        visible: true,
        locked: false,
        opacity: 1,
        blendMode: 'normal',
        position: { x: 0, y: 0, width: 100, height: 100 },
        data: {
          type: 'image',
          imageUrl: '/apps/default-cover.jpg'
        } as BackgroundLayer
      },
      // Title — deep blue-purple sky at the very top of the illustration
      {
        id: `layer-title-${t + 1}`,
        type: 'text',
        visible: true,
        locked: false,
        opacity: 1,
        blendMode: 'normal',
        position: { x: 5, y: 28, width: 90, height: 16 },
        data: {
          text: title,
          role: 'title',
          font: 'Playfair Display',
          fontSize: 210,
          fontWeight: 'bold',
          color: '#ffffff',
          align: 'center',
          transform: 'uppercase',
          lineHeight: 1.1,
          letterSpacing: 3,
          shadow: { x: 0, y: 2, blur: 10, color: 'rgba(0,0,0,0.35)' }
        } as TextLayer
      },
      // Author — dark green tree frame at the very bottom
      {
        id: `layer-author-${t + 2}`,
        type: 'text',
        visible: true,
        locked: false,
        opacity: 1,
        blendMode: 'normal',
        position: { x: 8, y: 64, width: 84, height: 8 },
        data: {
          text: author,
          role: 'author',
          font: 'Raleway',
          fontSize: 96,
          fontWeight: 'normal',
          color: '#ffffff',
          align: 'center',
          transform: 'none',
          lineHeight: 1.4,
          letterSpacing: 10,
          shadow: { x: 0, y: 1, blur: 8, color: 'rgba(0,0,0,0.35)' }
        } as TextLayer
      }
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: 1
  };
}

export interface ContentBlock {
  id: string;
  type: 'heading' | 'paragraph' | 'inline_image';
  text?: string;
  level?: 1 | 2 | 3;
  style?: {
    bold?: boolean;
    italic?: boolean;
    color?: string;
    fontSize?: number;
  };
  // inline_image fields
  imageUrl?: string;
  imageSource?: 'grok' | 'unsplash';
  imagePrompt?: string;
  imageQuery?: string;
}

export interface WholePageArt {
  id: string;
  pinnedPageNumber: number;
  imageUrl?: string;
  imageSource: 'grok' | 'unsplash';
  imagePrompt?: string;
  imageQuery?: string;
}

export interface ComputedPage {
  pageNumber: number;
  chapterId: string;
  chapterTitle: string;
  blocks: Array<{
    blockId: string;
    type: 'heading' | 'paragraph' | 'inline_image';
    level?: number;
    text?: string;
    style?: object;
    imageUrl?: string;
  }>;
}

export interface Chapter {
  id: string;
  title: string;
  content?: string;
  blocks?: ContentBlock[];
  documentId?: string;
  startPage?: number;
  wordCount?: number;
  order: number;
  contentLockedAt?: string;
}

export interface FormatSettings {
  pageSize: string;
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    bottom: number;
    inside: number;
    outside: number;
  };
  bodyFont: string;
  bodyFontSize: number;
  lineHeight: number;
  headingFont: string;
  headingFontSize: number;
  chapterPageBreak: boolean;
  header: {
    enabled: boolean;
    content: string;
    position: 'top' | 'bottom';
  };
  footer: {
    enabled: boolean;
    content: string;
    position: 'top' | 'bottom';
  };
  tableOfContents: {
    enabled: boolean;
    style: 'standard' | 'detailed';
    title: string;
    showPageNumbers: boolean;
    dotLeader: 'none' | 'dots' | 'dashes';
  };
}

export interface BookImage {
  id: string;
  pageIndex: number;
  chapterIndex?: number;
  imageUrl?: string;
  imageSource: 'grok' | 'unsplash';
  prompt?: string;
  query?: string;
  generatedAt?: string;
}

export interface ImageConfig {
  frequency: 'page' | 'every5' | 'chapter';
  defaultSource: 'grok' | 'unsplash';
  defaultPrompt?: string;
  defaultQuery?: string;
}

function generateBlockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export class BookFormatterApplication extends BaseApplication {
  // Book-specific Y.js maps
  private yFormatSettings: Y.Map<any> | null = null;
  private yChapters: Y.Array<any> | null = null;
  private yMetadata: Y.Map<any> | null = null;
  private yCover: Y.Map<any> | null = null;
  private yImages: Y.Array<any> | null = null;
  private yImageConfig: Y.Map<any> | null = null;
  private yPages: Y.Array<any> | null = null;

  // Sequence counter — stale repagination responses are discarded
  private repaginationSeq = 0;
  // Debounce timer — multi-chapter imports fire only one backend request
  private repaginationTimer: ReturnType<typeof setTimeout> | null = null;
  // Set to true during manual reimport so auto-sync does not race with it
  private _isReimporting = false;

  constructor() {
    super();
    console.log('[BookFormatterApplication] Constructor');
  }
  
  /**
   * Get the entity ID (public accessor)
   */
  getEntityId(): string | undefined {
    return this.entityId;
  }
  
  /**
   * Get application configuration
   */
  protected getConfig(): AppConfig {
    return BOOK_FORMATTER_APP_CONFIG;
  }
  
  /**
   * Load book project from backend
   */
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    console.log('[BookFormatterApplication] Loading book project:', entityId);
    
    try {
      // 1. Fetch entity from backend
      const entity = await entityService.get(entityId);
      
      if (!entity) {
        throw new Error(`Book project not found: ${entityId}`);
      }
      
      console.log('[BookFormatterApplication] Entity fetched:', {
        id: entity._id,
        type: entity.entityType,
        hasYjsState: !!entity.skeleton?.base64
      });
      
      // 2. Initialize base
      await this.initialize(entityId);
      
      // 3. Create Y.Doc
      this.ydoc = new Y.Doc();
      
      // 4. Load existing state if available
      if (entity.skeleton?.base64) {
        this.loadBase64State(entity.skeleton.base64);
      }
      
      // 5. Get content fragment (manuscript content)
      this.yxml = this.ydoc.getXmlFragment('prosemirror');
      
      // 6. Get book-specific Y.js structures
      this.yFormatSettings = this.ydoc.getMap('formatSettings');
      this.yChapters = this.ydoc.getArray('chapters');
      this.yMetadata = this.ydoc.getMap('metadata');
      this.yCover = this.ydoc.getMap('cover');
      this.yImages = this.ydoc.getArray('images');
      this.yImageConfig = this.ydoc.getMap('imageConfig');
      this.yPages = this.ydoc.getArray('computedPages');
      
      // 7. Initialize empty if NO existing state
      if (this.yxml.length === 0 && !entity.skeleton?.base64) {
        this.initializeEmptyBook(entity.skeleton as BookProjectSkeleton, entity.name);
      }
      
      // 8. Build indices
      await this.buildCoreIndices();
      
      // 9. Setup auto-save
      this.setupAutoSave();
      
      // 10. Optional: Setup collaboration
      if (!options.offline) {
        await this.setupCollaborationWithDoc(entityId, this.ydoc);
      }
      
      console.log('[BookFormatterApplication] ✅ Book project loaded', {
        entityId,
        nodes: this.indexManager.size(),
        chapters: this.yChapters?.length || 0,
        hasExistingState: !!entity.skeleton?.base64,
        offline: options.offline
      });
      
    } catch (error) {
      console.error('[BookFormatterApplication] Failed to load:', error);
      throw error;
    }
  }
  
  /**
   * Initialize empty book project
   */
  private initializeEmptyBook(skeleton?: BookProjectSkeleton, entityName?: string): void {
    console.log('[BookFormatterApplication] Initializing empty book');
    
    if (!this.ydoc || !this.yxml || !this.yFormatSettings || !this.yChapters || !this.yMetadata || !this.yCover) {
      throw new Error('Y.js structures not initialized');
    }
    
    // Initialize empty ProseMirror document
    this.ydoc.transact(() => {
      const paragraph = new Y.XmlElement('paragraph');
      paragraph.insert(0, [new Y.XmlText('')]);
      this.yxml!.insert(0, [paragraph]);
    }, 'local');
    
    // Initialize format settings
    const defaultSettings = skeleton?.formatSettings || DEFAULT_FORMAT_SETTINGS;
    this.yFormatSettings.set('pageSize', defaultSettings?.pageSize || '6x9');
    this.yFormatSettings.set('orientation', defaultSettings?.orientation || 'portrait');
    this.yFormatSettings.set('margins', defaultSettings?.margins || {
      top: 0.75,
      bottom: 0.75,
      inside: 0.75,
      outside: 0.5
    });
    this.yFormatSettings.set('bodyFont', defaultSettings?.bodyFont || 'Garamond');
    this.yFormatSettings.set('bodyFontSize', defaultSettings?.bodyFontSize || 11);
    this.yFormatSettings.set('lineHeight', defaultSettings?.lineHeight || 1.5);
    this.yFormatSettings.set('headingFont', defaultSettings?.headingFont || 'Garamond');
    this.yFormatSettings.set('headingFontSize', defaultSettings?.headingFontSize || 16);
    this.yFormatSettings.set('chapterPageBreak', defaultSettings?.chapterPageBreak !== false);
    this.yFormatSettings.set('header', defaultSettings?.header || {
      enabled: false,
      content: '',
      position: 'top'
    });
    this.yFormatSettings.set('footer', defaultSettings?.footer || {
      enabled: true,
      content: 'pageNumber',
      position: 'bottom'
    });
    this.yFormatSettings.set('tableOfContents', defaultSettings?.tableOfContents || {
      enabled: true,
      style: 'standard',
      title: 'Table of Contents',
      showPageNumbers: true,
      dotLeader: 'dots'
    });
    
    // Initialize metadata — prefer skeleton values, fall back to entity name / logged-in user
    const currentUser = authService.getCurrentUser();
    this.yMetadata.set('title', skeleton?.title || entityName || 'Untitled Book');
    this.yMetadata.set('author', skeleton?.author || currentUser?.name || '');
    this.yMetadata.set('subtitle', skeleton?.subtitle || '');
    this.yMetadata.set('isbn', skeleton?.isbn || '');
    this.yMetadata.set('publisher', skeleton?.publisher || '');
    this.yMetadata.set('description', skeleton?.description || '');
    this.yMetadata.set('keywords', skeleton?.keywords || []);
    this.yMetadata.set('genre', skeleton?.genre || '');
    
    // Initialize chapters if provided
    if (skeleton?.chapters && skeleton.chapters.length > 0) {
      skeleton.chapters.forEach(chapter => {
        this.yChapters!.push([chapter]);
      });
    }
    
    // Initialize default cover with title and author
    const entityId = this.getEntityId();
    if (entityId) {
      const currentUser = authService.getCurrentUser();
      const title = skeleton?.title || entityName || 'Untitled Book';
      const author = skeleton?.author || currentUser?.name || 'Author Name';
      const defaultCover = createDefaultCover(entityId, title, author);
      this.saveCover(defaultCover);
      console.log('[BookFormatterApplication] ✅ Initialized default cover with title and author');
    }
  }
  
  /**
   * Setup collaboration provider
   */
  private async setupCollaborationWithDoc(entityId: string, ydoc: Y.Doc): Promise<void> {
    console.log('[BookFormatterApplication] Setting up collaboration');
    
    const authUser = authService.getCurrentUser();
    
    if (!authUser) {
      console.warn('[BookFormatterApplication] Not authenticated - skipping collaboration');
      return;
    }
    
    const currentUser = {
      id: authUser.id,
      name: authUser.name || authUser.email || 'User',
      color: generateUserColor(authUser.id)
    };
    
    try {
      this.collaborationProvider = createCollaborationProvider({
        documentId: `doc:${entityId}`,
        user: currentUser,
        websocketUrl: this.getWebSocketUrl(),
        authToken: '',
        offline: false,
        doc: ydoc,
      });
      
      this.collaborationProvider.connect();
      
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('[BookFormatterApplication] Sync timeout - proceeding anyway');
          cleanup();
          resolve();
        }, 5000);

        const onSynced = () => {
          clearTimeout(timeout);
          cleanup();
          console.log('[BookFormatterApplication] ✅ Document synced');
          resolve();
        };

        const onError = (error: Error) => {
          clearTimeout(timeout);
          cleanup();
          console.warn('[BookFormatterApplication] Sync error (non-fatal):', error);
          resolve();
        };

        const cleanup = () => {
          this.collaborationProvider!.off('synced', onSynced);
          this.collaborationProvider!.off('error', onError);
        };

        this.collaborationProvider!.on('synced', onSynced);
        this.collaborationProvider!.on('error', onError);
      });
    } catch (error) {
      console.warn('[BookFormatterApplication] Collaboration setup failed (non-fatal):', error);
      // Don't throw - app can work without collaboration
    }
  }
  
  /**
   * Get format settings
   */
  getFormatSettings(): FormatSettings | null {
    if (!this.yFormatSettings) return null;
    
    return {
      pageSize: this.yFormatSettings.get('pageSize') || '6x9',
      orientation: this.yFormatSettings.get('orientation') || 'portrait',
      margins: this.yFormatSettings.get('margins') || {
        top: 0.75,
        bottom: 0.75,
        inside: 0.75,
        outside: 0.5
      },
      bodyFont: this.yFormatSettings.get('bodyFont') || 'Garamond',
      bodyFontSize: this.yFormatSettings.get('bodyFontSize') || 11,
      lineHeight: this.yFormatSettings.get('lineHeight') || 1.5,
      headingFont: this.yFormatSettings.get('headingFont') || 'Garamond',
      headingFontSize: this.yFormatSettings.get('headingFontSize') || 16,
      chapterPageBreak: this.yFormatSettings.get('chapterPageBreak') !== false,
      header: this.yFormatSettings.get('header') || {
        enabled: false,
        content: '',
        position: 'top'
      },
      footer: this.yFormatSettings.get('footer') || {
        enabled: true,
        content: 'pageNumber',
        position: 'bottom'
      },
      tableOfContents: {
        enabled: true,
        style: 'standard',
        title: 'Table of Contents',
        showPageNumbers: true,
        dotLeader: 'dots',
        ...(this.yFormatSettings.get('tableOfContents') || {}),
      }
    };
  }
  
  /**
   * Update format settings
   */
  updateFormatSettings(settings: Partial<FormatSettings>): void {
    if (!this.yFormatSettings) {
      throw new Error('Format settings not initialized');
    }
    
    this.ydoc!.transact(() => {
      Object.entries(settings).forEach(([key, value]) => {
        // Deep-merge nested objects (e.g. margins, header, footer) so that
        // callers passing partial sub-objects don't overwrite sibling fields.
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          const existing = this.yFormatSettings!.get(key);
          const merged = (existing !== null && typeof existing === 'object' && !Array.isArray(existing))
            ? { ...(existing as object), ...value }
            : value;
          this.yFormatSettings!.set(key, merged);
        } else {
          this.yFormatSettings!.set(key, value);
        }
      });
    }, 'local');
  }
  
  /**
   * Get chapters
   */
  getChapters(): Chapter[] {
    if (!this.yChapters) return [];
    return this.yChapters.toArray() as Chapter[];
  }
  
  /**
   * Remove duplicate chapters that share the same documentId, keeping the one
   * with content. Called once after load to clean up any previously-persisted
   * duplicates.
   */
  deduplicateChapters(): void {
    if (!this.yChapters) return;

    const chapters = this.yChapters.toArray() as Chapter[];
    const seen = new Map<string, { index: number; chapter: Chapter }>();
    const indicesToRemove: number[] = [];

    chapters.forEach((ch, i) => {
      if (!ch.documentId) return; // manual chapters — keep as-is
      const existing = seen.get(ch.documentId);
      if (!existing) {
        seen.set(ch.documentId, { index: i, chapter: ch });
        return;
      }
      // Duplicate found — keep whichever has content, prefer later one if both do
      const keepExisting =
        (existing.chapter.content || '').trim().length > 0 &&
        (ch.content || '').trim().length === 0;
      if (keepExisting) {
        indicesToRemove.push(i);
      } else {
        indicesToRemove.push(existing.index);
        seen.set(ch.documentId, { index: i, chapter: ch });
      }
    });

    if (indicesToRemove.length === 0) return;

    console.log('[BookFormatterApplication] Deduplicating chapters, removing', indicesToRemove.length, 'duplicate(s)');
    // Remove from highest index downward so indices stay valid
    const sorted = [...indicesToRemove].sort((a, b) => b - a);
    this.ydoc!.transact(() => {
      for (const idx of sorted) {
        this.yChapters!.delete(idx, 1);
      }
    }, 'local');
  }

  /**
   * Add chapter
   */
  addChapter(chapter: Chapter): void {
    if (!this.yChapters) {
      throw new Error('Chapters not initialized');
    }
    
    this.ydoc!.transact(() => {
      this.yChapters!.push([chapter]);
    }, 'local');
  }
  
  /**
   * Add (or update) a chapter from a River document.
   *
   * Extraction order:
   *   1. skeleton.base64 from MongoDB (fast, offline-safe)
   *   2. If that yields empty content, sync the document live via Y.js WebSocket
   *   3. markdownExport as a last resort
   */
  async addChapterFromDocument(title: string, documentEntity: any): Promise<void> {
    if (!this.yChapters) {
      throw new Error('Chapters not initialized');
    }

    const docId = documentEntity._id || documentEntity.id;
    let content = '';
    let wordCount = 0;

    console.log('[BookFormatterApplication] addChapterFromDocument:', {
      title,
      docId,
      hasBase64: !!documentEntity.skeleton?.base64,
      base64Length: documentEntity.skeleton?.base64?.length ?? 0,
      hasHtmlContent: !!documentEntity.skeleton?.content,
      htmlContentLength: documentEntity.skeleton?.content?.length ?? 0,
      hasMarkdownExport: !!documentEntity.skeleton?.markdownExport,
    });

    // 1. Try skeleton.base64 (MongoDB snapshot)
    if (documentEntity.skeleton?.base64) {
      const docYDoc = new Y.Doc();
      try {
        const state = Uint8Array.from(atob(documentEntity.skeleton.base64), c => c.charCodeAt(0));
        console.log('[BookFormatterApplication] Decoded base64 to Uint8Array, byteLength:', state.byteLength);

        try {
          Y.applyUpdateV2(docYDoc, state);
          console.log('[BookFormatterApplication] applyUpdateV2 succeeded');
        } catch (v2Error) {
          console.warn('[BookFormatterApplication] Y.js V2 decode failed, trying V1:', v2Error);
          Y.applyUpdate(docYDoc, state);
          console.log('[BookFormatterApplication] applyUpdate (V1) succeeded');
        }

        // Log every shared type in the Y.Doc so we can catch content stored under
        // a key other than 'prosemirror' (e.g. 'default', 'content', 'text', etc.)
        const allYjsKeys = Array.from((docYDoc as any).share?.keys() ?? []);
        console.log('[BookFormatterApplication] All Y.js shared type keys in skeleton.base64:', allYjsKeys);

        const docXml = docYDoc.getXmlFragment('prosemirror');
        const rootChildren = (docXml as any).toArray() as Array<Y.XmlText | Y.XmlElement>;
        console.log('[BookFormatterApplication] Y.js fragment info:', {
          fragmentLength: docXml.length,
          rootChildCount: rootChildren.length,
          rootChildTypes: rootChildren.map((c: any) => ({
            type: c.constructor?.name,
            nodeName: c.nodeName,
          })),
        });

        content = extractYjsText(docXml);
        wordCount = content.split(/\s+/).filter(w => w.length > 0).length;

        console.log('[BookFormatterApplication] skeleton.base64 extraction:', {
          contentLength: content.length,
          wordCount,
          preview: content.substring(0, 200),
        });
      } catch (error) {
        console.error('[BookFormatterApplication] Error extracting Y.js content from base64:', error);
      } finally {
        docYDoc.destroy();
      }
    }

    // 2. skeleton.content — HTML string saved by the DocsApp to the backend
    if (content.trim().length === 0 && documentEntity.skeleton?.content) {
      const html = documentEntity.skeleton.content as string;
      console.log('[BookFormatterApplication] skeleton.base64 empty — extracting from skeleton.content HTML, length:', html.length);
      try {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(html, 'text/html');
        // Walk the DOM to preserve paragraph breaks
        const parts: string[] = [];
        htmlDoc.body?.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li').forEach((el) => {
          const text = el.textContent?.trim();
          if (text) parts.push(text);
        });
        content = parts.length > 0 ? parts.join('\n\n') : (htmlDoc.body?.textContent?.trim() ?? '');
        wordCount = content.split(/\s+/).filter(w => w.length > 0).length;
        console.log('[BookFormatterApplication] ✅ skeleton.content extraction:', {
          contentLength: content.length,
          wordCount,
          preview: content.substring(0, 200),
        });
      } catch (htmlError) {
        console.error('[BookFormatterApplication] Error extracting skeleton.content HTML:', htmlError);
      }
    }

    // 3. Last resort: markdownExport
    if (content.trim().length === 0 && documentEntity.skeleton?.markdownExport) {
      content = documentEntity.skeleton.markdownExport;
      wordCount = content.split(/\s+/).filter(w => w.length > 0).length;
      console.log('[BookFormatterApplication] ✅ Using markdownExport fallback, length:', content.length);
    }

    if (content.trim().length === 0) {
      console.warn('[BookFormatterApplication] ⚠️ All extraction methods returned empty content for:', title);
    }

    // Guard: app may have been destroyed while awaiting the WebSocket sync
    if (!this.yChapters) return;

    const existingChapters = this.yChapters.toArray() as Chapter[];

    // Primary match: same documentId (same doc, content refresh)
    let existingIndex = existingChapters.findIndex(ch => ch.documentId === docId);

    // Secondary match: same title where existing chapter has no content.
    // This handles switching from an old empty doc-set to new content-filled docs
    // (different documentIds, same chapter titles).
    if (existingIndex === -1 && content.trim().length > 0) {
      existingIndex = existingChapters.findIndex(
        ch => ch.title?.trim().toLowerCase() === title.trim().toLowerCase()
          && (ch.content || '').trim().length === 0,
      );
      if (existingIndex >= 0) {
        console.log('[BookFormatterApplication] Title-match replacing empty chapter:', {
          title,
          oldDocId: existingChapters[existingIndex].documentId,
          newDocId: docId,
        });
      }
    }

    if (existingIndex >= 0) {
      if (content.trim().length > 0) {
        console.log('[BookFormatterApplication] Updating existing chapter:', { title, wordCount, contentPreview: content.substring(0, 100) });
        const blocks = this.parseContentToBlocks(content, title);
        this.ydoc!.transact(() => {
          const existing = existingChapters[existingIndex];
          this.yChapters!.delete(existingIndex, 1);
          this.yChapters!.insert(existingIndex, [{
            ...existing,
            title,
            content,
            blocks,
            contentLockedAt: new Date().toISOString(),
            wordCount,
            documentId: docId,
          }]);
        }, 'local');
        this.setContentImported(true);
        this.triggerRepagination().catch(err =>
          console.warn('[BookFormatterApplication] Repagination after update failed:', err)
        );
      } else {
        console.log('[BookFormatterApplication] Skipping update — all extraction methods empty, keeping existing content');
      }
      return;
    }

    const chapter: Chapter = {
      id: `chapter-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      title,
      content,
      blocks: this.parseContentToBlocks(content, title),
      contentLockedAt: new Date().toISOString(),
      documentId: docId,
      order: this.yChapters.length,
      wordCount,
    };

    this.ydoc!.transact(() => {
      this.yChapters!.push([chapter]);
    }, 'local');

    // Only lock content when real content was actually imported
    if (content.trim().length > 0) {
      this.setContentImported(true);
    }
    this.triggerRepagination().catch(err =>
      console.warn('[BookFormatterApplication] Repagination after add failed:', err)
    );

    console.log('[BookFormatterApplication] Chapter added from document:', {
      title,
      wordCount,
      hasContent: content.trim().length > 0,
      contentPreview: content.substring(0, 100),
      documentId: docId,
    });
  }

  /**
   * Fetch the live Y.js content for a River document by connecting to the
   * same Y.js WebSocket server and waiting for the initial sync.
   * Resolves with extracted plain text, or rejects on timeout/error.
   */
  private fetchContentFromWebSocket(docId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const tempDoc = new Y.Doc();
      const wsUrl = this.getWebSocketUrl();
      // River documents are routed by the 'doc:' prefix on the WebSocket server.
      // Without it the server treats the room as non-persisted and returns an empty doc.
      const roomName = `doc:${docId}`;
      console.log('[BookFormatterApplication] fetchContentFromWebSocket connecting to room:', roomName, 'wsUrl:', wsUrl);

      let provider: WebsocketProvider;
      try {
        provider = new WebsocketProvider(wsUrl, roomName, tempDoc, { connect: true });
      } catch (err) {
        tempDoc.destroy();
        reject(err);
        return;
      }

      const cleanup = () => {
        try { provider.destroy(); } catch (_) {}
        tempDoc.destroy();
      };

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error(`WebSocket sync timeout for doc ${docId}`));
      }, 8000);

      provider.on('sync', (synced: boolean) => {
        if (!synced) return;
        clearTimeout(timeout);
        try {
          const fragment = tempDoc.getXmlFragment('prosemirror');
          const text = extractYjsText(fragment);
          cleanup();
          resolve(text);
        } catch (err) {
          cleanup();
          reject(err);
        }
      });

      provider.on('connection-error', (event: Event) => {
        clearTimeout(timeout);
        cleanup();
        reject(new Error(`WebSocket connection error for doc ${docId}`));
      });
    });
  }
  
  /**
   * Update chapter
   */
  updateChapter(chapterId: string, updates: Partial<Chapter>): void {
    if (!this.yChapters) {
      throw new Error('Chapters not initialized');
    }
    
    this.ydoc!.transact(() => {
      const chapters = this.yChapters!.toArray() as Chapter[];
      const index = chapters.findIndex(c => c.id === chapterId);
      
      if (index !== -1) {
        const updated = { ...chapters[index], ...updates };
        this.yChapters!.delete(index, 1);
        this.yChapters!.insert(index, [updated]);
      }
    }, 'local');
  }
  
  /**
   * Subscribe to chapter changes (Y.js array observation).
   * The callback fires whenever chapters are inserted, deleted, or replaced —
   * including updates that arrive over the websocket after initial load.
   * Returns an unsubscribe function.
   */
  observeChapters(callback: () => void): () => void {
    if (!this.yChapters) return () => {};
    const handler = () => callback();
    this.yChapters.observe(handler);
    return () => this.yChapters?.unobserve(handler);
  }

  /**
   * Remove chapter
   */
  removeChapter(chapterId: string): void {
    if (!this.yChapters) {
      throw new Error('Chapters not initialized');
    }
    
    this.ydoc!.transact(() => {
      const chapters = this.yChapters!.toArray() as Chapter[];
      const index = chapters.findIndex(c => c.id === chapterId);
      
      if (index !== -1) {
        this.yChapters!.delete(index, 1);
      }
    }, 'local');
  }
  
  /**
   * Reorder chapters
   */
  reorderChapters(chapterIds: string[]): void {
    if (!this.yChapters) {
      throw new Error('Chapters not initialized');
    }
    
    this.ydoc!.transact(() => {
      const chapters = this.yChapters!.toArray() as Chapter[];
      const reordered = chapterIds.map((id, index) => {
        const chapter = chapters.find(c => c.id === id);
        return chapter ? { ...chapter, order: index } : null;
      }).filter(Boolean) as Chapter[];
      
      this.yChapters!.delete(0, chapters.length);
      this.yChapters!.insert(0, reordered);
    }, 'local');
  }
  
  /**
   * Get book metadata
   */
  getMetadata(): Record<string, any> {
    if (!this.yMetadata) return {};
    
    const metadata: Record<string, any> = {};
    this.yMetadata.forEach((value, key) => {
      metadata[key] = value;
    });
    return metadata;
  }
  
  /**
   * Update book metadata
   */
  updateMetadata(updates: Record<string, any>): void {
    if (!this.yMetadata) {
      throw new Error('Metadata not initialized');
    }
    
    this.ydoc!.transact(() => {
      Object.entries(updates).forEach(([key, value]) => {
        this.yMetadata!.set(key, value);
      });
    }, 'local');
  }
  
  /**
   * Get book cover
   */
  getCover(): BookCover | null {
    if (!this.yCover || this.yCover.size === 0) return null;
    return this.yCover.toJSON() as BookCover;
  }

  /**
   * Save book cover
   * IMPORTANT: Strips large base64 data URLs to prevent Y.js document bloat
   */
  saveCover(cover: BookCover): void {
    if (!this.yCover) {
      throw new Error('Cover storage not initialized');
    }

    // Create a sanitized copy that strips base64 data URLs
    const sanitizedCover = this.sanitizeCoverForStorage(cover);

    this.ydoc!.transact(() => {
      // Clear existing cover data
      const keys = Array.from(this.yCover!.keys());
      keys.forEach(key => this.yCover!.delete(key));

      // Set new cover data (sanitized)
      Object.entries(sanitizedCover).forEach(([key, value]) => {
        this.yCover!.set(key, value);
      });
    }, 'local');
    
    console.log('[BookFormatterApplication] Saved cover (sanitized):', cover.id);
  }

  /**
   * Sanitize cover data by removing base64 data URLs
   * Only keep S3 URLs or other references
   */
  private sanitizeCoverForStorage(cover: BookCover): BookCover {
    const sanitizedLayers = cover.layers.map(layer => {
      const sanitizedLayer = { ...layer };
      
      // Handle background layers with images
      if (layer.type === 'background' && layer.data) {
        const bgData = layer.data as any;
        if (bgData.imageUrl && bgData.imageUrl.startsWith('data:')) {
          // Strip data URLs - they're too large for Y.js
          console.warn('[BookFormatterApplication] Stripping base64 data URL from background layer:', layer.id);
          sanitizedLayer.data = {
            ...bgData,
            imageUrl: undefined // Remove data URL
          };
        }
      }
      
      // Handle image layers
      if (layer.type === 'image' && layer.data) {
        const imgData = layer.data as any;
        if (imgData.url && imgData.url.startsWith('data:')) {
          console.warn('[BookFormatterApplication] Stripping base64 data URL from image layer:', layer.id);
          sanitizedLayer.data = {
            ...imgData,
            url: undefined // Remove data URL
          };
        }
      }
      
      return sanitizedLayer;
    });

    return {
      ...cover,
      layers: sanitizedLayers
    };
  }

  /**
   * Get WebSocket URL
   */
  protected getWebSocketUrl(): string {
    return WEBSOCKET_URL || 'ws://localhost:1234';
  }

  // ─────────────────────────────────────────────────────────────────────────
  // IMAGE CONFIG
  // ─────────────────────────────────────────────────────────────────────────

  getImageConfig(): ImageConfig {
    const defaults: ImageConfig = {
      frequency: 'chapter',
      defaultSource: 'grok',
      defaultPrompt: '',
      defaultQuery: '',
    };
    if (!this.yImageConfig || this.yImageConfig.size === 0) return defaults;
    return {
      frequency: (this.yImageConfig.get('frequency') as ImageConfig['frequency']) || defaults.frequency,
      defaultSource: (this.yImageConfig.get('defaultSource') as ImageConfig['defaultSource']) || defaults.defaultSource,
      defaultPrompt: this.yImageConfig.get('defaultPrompt') || defaults.defaultPrompt,
      defaultQuery: this.yImageConfig.get('defaultQuery') || defaults.defaultQuery,
    };
  }

  setImageConfig(config: Partial<ImageConfig>): void {
    if (!this.yImageConfig) throw new Error('Image config not initialized');
    this.ydoc!.transact(() => {
      Object.entries(config).forEach(([key, value]) => {
        this.yImageConfig!.set(key, value);
      });
    }, 'local');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // IMAGES
  // ─────────────────────────────────────────────────────────────────────────

  getImages(): BookImage[] {
    if (!this.yImages) return [];
    return this.yImages.toArray() as BookImage[];
  }

  addImage(image: BookImage): void {
    if (!this.yImages) throw new Error('Images not initialized');
    this.ydoc!.transact(() => {
      this.yImages!.push([image]);
    }, 'local');
  }

  updateImage(imageId: string, updates: Partial<BookImage>): void {
    if (!this.yImages) throw new Error('Images not initialized');
    this.ydoc!.transact(() => {
      const images = this.yImages!.toArray() as BookImage[];
      const index = images.findIndex(img => img.id === imageId);
      if (index !== -1) {
        const updated = { ...images[index], ...updates };
        this.yImages!.delete(index, 1);
        this.yImages!.insert(index, [updated]);
      }
    }, 'local');
  }

  removeImage(imageId: string): void {
    if (!this.yImages) throw new Error('Images not initialized');
    this.ydoc!.transact(() => {
      const images = this.yImages!.toArray() as BookImage[];
      const index = images.findIndex(img => img.id === imageId);
      if (index !== -1) this.yImages!.delete(index, 1);
    }, 'local');
  }

  clearImages(): void {
    if (!this.yImages) throw new Error('Images not initialized');
    this.ydoc!.transact(() => {
      if (this.yImages!.length > 0) this.yImages!.delete(0, this.yImages!.length);
    }, 'local');
  }

  observeImages(callback: () => void): () => void {
    if (!this.yImages) return () => {};
    const handler = () => callback();
    this.yImages.observe(handler);
    return () => this.yImages?.unobserve(handler);
  }

  observeMetadata(callback: () => void): () => void {
    if (!this.yMetadata) return () => {};
    const handler = () => callback();
    this.yMetadata.observe(handler);
    return () => this.yMetadata?.unobserve(handler);
  }

  observeCover(callback: () => void): () => void {
    if (!this.yCover) return () => {};
    const handler = () => callback();
    this.yCover.observe(handler);
    return () => this.yCover?.unobserve(handler);
  }

  observeFormatSettings(callback: () => void): () => void {
    if (!this.yFormatSettings) return () => {};
    const handler = () => callback();
    this.yFormatSettings.observe(handler);
    return () => this.yFormatSettings?.unobserve(handler);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // WHOLE-PAGE ART (pinned, does not reflow with text)
  // ─────────────────────────────────────────────────────────────────────────

  getWholePageArt(): WholePageArt[] {
    if (!this.yImages) return [];
    return (this.yImages.toArray() as any[]).reduce<WholePageArt[]>((acc, img) => {
      if (img.pinnedPageNumber !== undefined) {
        acc.push(img as WholePageArt);
      } else if (img.pageIndex !== undefined && img.imageUrl) {
        // Convert legacy BookImage (pageIndex-based) to WholePageArt shape
        acc.push({
          id: img.id,
          pinnedPageNumber: img.pageIndex + 1,
          imageUrl: img.imageUrl,
          imageSource: img.source || 'grok',
          imagePrompt: img.prompt,
          imageQuery: img.query,
        });
      }
      return acc;
    }, []);
  }

  addWholePageArt(art: WholePageArt): void {
    if (!this.yImages) throw new Error('Images not initialized');
    this.ydoc!.transact(() => {
      this.yImages!.push([art]);
    }, 'local');
  }

  updateWholePageArt(id: string, updates: Partial<WholePageArt>): void {
    if (!this.yImages) throw new Error('Images not initialized');
    this.ydoc!.transact(() => {
      const all = this.yImages!.toArray() as any[];
      const index = all.findIndex(img => img.id === id);
      if (index !== -1) {
        this.yImages!.delete(index, 1);
        this.yImages!.insert(index, [{ ...all[index], ...updates }]);
      }
    }, 'local');
  }

  removeWholePageArt(id: string): void {
    if (!this.yImages) throw new Error('Images not initialized');
    this.ydoc!.transact(() => {
      const all = this.yImages!.toArray() as any[];
      const index = all.findIndex(img => img.id === id);
      if (index !== -1) this.yImages!.delete(index, 1);
    }, 'local');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CONTENT BLOCKS — per-block style updates and inline images
  // ─────────────────────────────────────────────────────────────────────────

  updateBlockStyle(chapterId: string, blockId: string, style: ContentBlock['style']): void {
    if (!this.yChapters) throw new Error('Chapters not initialized');
    this.ydoc!.transact(() => {
      const chapters = this.yChapters!.toArray() as Chapter[];
      const chapterIndex = chapters.findIndex(ch => ch.id === chapterId);
      if (chapterIndex === -1) return;
      const chapter = chapters[chapterIndex];
      const blocks = (chapter.blocks || []).map(b =>
        b.id === blockId ? { ...b, style: { ...(b.style || {}), ...style } } : b
      );
      this.yChapters!.delete(chapterIndex, 1);
      this.yChapters!.insert(chapterIndex, [{ ...chapter, blocks }]);
    }, 'local');
  }

  addInlineImage(chapterId: string, afterBlockId: string | null, block: ContentBlock): void {
    if (!this.yChapters) throw new Error('Chapters not initialized');
    this.ydoc!.transact(() => {
      const chapters = this.yChapters!.toArray() as Chapter[];
      const chapterIndex = chapters.findIndex(ch => ch.id === chapterId);
      if (chapterIndex === -1) return;
      const chapter = chapters[chapterIndex];
      const blocks = [...(chapter.blocks || [])];
      if (afterBlockId === null) {
        blocks.push(block);
      } else {
        const afterIndex = blocks.findIndex(b => b.id === afterBlockId);
        blocks.splice(afterIndex >= 0 ? afterIndex + 1 : blocks.length, 0, block);
      }
      this.yChapters!.delete(chapterIndex, 1);
      this.yChapters!.insert(chapterIndex, [{ ...chapter, blocks }]);
    }, 'local');
    this.triggerRepagination().catch(err =>
      console.warn('[BookFormatterApplication] Repagination after inline image failed:', err)
    );
  }

  removeInlineImage(chapterId: string, blockId: string): void {
    if (!this.yChapters) throw new Error('Chapters not initialized');
    this.ydoc!.transact(() => {
      const chapters = this.yChapters!.toArray() as Chapter[];
      const chapterIndex = chapters.findIndex(ch => ch.id === chapterId);
      if (chapterIndex === -1) return;
      const chapter = chapters[chapterIndex];
      const blocks = (chapter.blocks || []).filter(b => b.id !== blockId);
      this.yChapters!.delete(chapterIndex, 1);
      this.yChapters!.insert(chapterIndex, [{ ...chapter, blocks }]);
    }, 'local');
    this.triggerRepagination().catch(err =>
      console.warn('[BookFormatterApplication] Repagination after inline image removal failed:', err)
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CONTENT LOCK
  // ─────────────────────────────────────────────────────────────────────────

  isContentImported(): boolean {
    if (!this.yMetadata) return false;
    return this.yMetadata.get('contentImported') === true;
  }

  setContentImported(v: boolean): void {
    if (!this.yMetadata) return;
    this.ydoc!.transact(() => {
      this.yMetadata!.set('contentImported', v);
    }, 'local');
  }

  clearContent(): void {
    if (!this.yChapters) return;
    this.ydoc!.transact(() => {
      if (this.yChapters!.length > 0) {
        this.yChapters!.delete(0, this.yChapters!.length);
      }
      if (this.yPages && this.yPages.length > 0) {
        this.yPages.delete(0, this.yPages.length);
      }
      if (this.yImages && this.yImages.length > 0) {
        this.yImages.delete(0, this.yImages.length);
      }
      if (this.yImageConfig) {
        this.yImageConfig.forEach((_: any, key: string) => this.yImageConfig!.delete(key));
      }
      if (this.yFormatSettings) {
        this.yFormatSettings.forEach((_: any, key: string) => this.yFormatSettings!.delete(key));
      }
      this.yMetadata?.set('contentImported', false);
    }, 'local');
  }

  /** True while a manual reimport is in progress — auto-sync should skip. */
  isReimporting(): boolean {
    return this._isReimporting;
  }

  setReimporting(v: boolean): void {
    this._isReimporting = v;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // COMPUTED PAGES (backend pagination result)
  // ─────────────────────────────────────────────────────────────────────────

  getComputedPages(): ComputedPage[] {
    if (!this.yPages) return [];
    return this.yPages.toArray() as ComputedPage[];
  }

  setComputedPages(pages: ComputedPage[]): void {
    if (!this.yPages) return;
    this.ydoc!.transact(() => {
      if (this.yPages!.length > 0) this.yPages!.delete(0, this.yPages!.length);
      if (pages.length > 0) this.yPages!.push(pages);
    }, 'local');
  }

  observePages(callback: () => void): () => void {
    if (!this.yPages) return () => {};
    const handler = () => callback();
    this.yPages.observe(handler);
    return () => this.yPages?.unobserve(handler);
  }

  /**
   * Schedule a repagination request, debounced by 200 ms so that bulk
   * chapter imports only fire a single backend call.  The sequence counter
   * ensures that stale out-of-order responses are silently dropped.
   */
  triggerRepagination(): Promise<void> {
    if (this.repaginationTimer !== null) {
      clearTimeout(this.repaginationTimer);
      this.repaginationTimer = null;
    }
    return new Promise<void>((resolve) => {
      this.repaginationTimer = setTimeout(() => {
        this.repaginationTimer = null;
        this._executeRepagination().then(resolve).catch(resolve);
      }, 200);
    });
  }

  private async _executeRepagination(): Promise<void> {
    const chapters = this.getChapters();
    const formatSettings = this.getFormatSettings();
    if (chapters.length === 0 || !formatSettings) return;

    const seq = ++this.repaginationSeq;
    const authToken = localStorage.getItem('authToken');
    const apiUrl = `${API_ORIGIN_URL}/api/book/paginate`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          chapters: chapters.map(ch => ({
            id: ch.id,
            title: ch.title,
            blocks: ch.blocks || [],
          })),
          formatSettings,
        }),
      });

      if (!response.ok) {
        console.warn('[BookFormatterApplication] Repagination request failed:', response.status);
        return;
      }

      const pages: ComputedPage[] = await response.json();

      // Discard if a newer request has already been dispatched
      if (seq !== this.repaginationSeq) {
        console.log('[BookFormatterApplication] Discarding stale repagination response (seq', seq, '< current', this.repaginationSeq, ')');
        return;
      }

      this.setComputedPages(pages);
      console.log('[BookFormatterApplication] ✅ Repaginated:', pages.length, 'page(s)');
    } catch (err) {
      console.warn('[BookFormatterApplication] Repagination error (non-fatal):', err);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CONTENT BLOCK PARSING (internal)
  // ─────────────────────────────────────────────────────────────────────────

  private parseContentToBlocks(text: string, title: string): ContentBlock[] {
    const blocks: ContentBlock[] = [];
    // Title heading is the first block
    blocks.push({ id: generateBlockId(), type: 'heading', level: 1, text: title });

    if (!text || text.trim().length === 0) return blocks;

    // Filter out document-structure labels like "(chapter)" that some editors insert
    const isStructuralLabel = (p: string) => /^\([a-z]+\)$/i.test(p);
    const paragraphs = text.split(/\n\n+/).map(p => p.replace(/\n/g, ' ').trim()).filter(p => p.length > 0 && !isStructuralLabel(p));
    for (const para of paragraphs) {
      if (para.startsWith('### ')) {
        blocks.push({ id: generateBlockId(), type: 'heading', level: 3, text: para.slice(4).trim() });
      } else if (para.startsWith('## ')) {
        blocks.push({ id: generateBlockId(), type: 'heading', level: 2, text: para.slice(3).trim() });
      } else if (para.startsWith('# ')) {
        blocks.push({ id: generateBlockId(), type: 'heading', level: 1, text: para.slice(2).trim() });
      } else {
        blocks.push({ id: generateBlockId(), type: 'paragraph', text: para });
      }
    }
    return blocks;
  }

  async destroy(): Promise<void> {
    if (this.repaginationTimer !== null) {
      clearTimeout(this.repaginationTimer);
      this.repaginationTimer = null;
    }
    await super.destroy();
  }
}
